import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { API_URL } from '../app/constants';

const SessionCreationContext = createContext();

export const useSessionCreation = () => {
  const context = useContext(SessionCreationContext);
  if (!context) {
    throw new Error('useSessionCreation must be used within a SessionCreationProvider');
  }
  return context;
};

export const SessionCreationProvider = ({ children }) => {
  const [status, setStatus] = useState('not_started'); // 'not_started' | 'creating' | 'completed'
  const [showModal, setShowModal] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const intervalRef = useRef(null);
  const attemptsRef = useRef(0);
  const maxAttempts = 30; // 1 minute safety timeout

  // Start polling for session creation status
  const startPolling = () => {
    if (isPolling) return; // Already polling
    
    console.log('SessionCreationContext: Starting polling');
    setIsPolling(true);
    attemptsRef.current = 0;
    
    const poll = async () => {
      try {
        const jwt = localStorage.getItem('jwt');
        if (!jwt) {
          console.log('SessionCreationContext: No JWT, stopping polling');
          stopPolling();
          return;
        }

        const response = await fetch(`${API_URL}/api/v1/panel/session-creation-status`, {
          headers: { 'Authorization': `Bearer ${jwt}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          console.log('SessionCreationContext: Status received:', data.status);
          
          setStatus(data.status);
          
          if (data.status === 'completed') {
            console.log('SessionCreationContext: Sessions completed, stopping polling');
            stopPolling();
            hideModal();
            
            // Don't redirect if user is on diagnostic-stats page (let them stay to see results)
            if (!window.location.pathname.includes('/diagnostic-stats')) {
              // Force hard reload to study_plan with cache busting
              setTimeout(() => {
                window.location.href = '/study_plan?t=' + Date.now();
              }, 500);
            }
          } else if (data.status === 'creating') {
            console.log('SessionCreationContext: Sessions being created');
          }
        } else {
          console.error('SessionCreationContext: API error:', response.status);
        }
        
        attemptsRef.current++;
        
        // Safety timeout
        if (attemptsRef.current >= maxAttempts) {
          console.log('SessionCreationContext: Safety timeout reached, stopping polling');
          stopPolling();
          setStatus('completed'); // Assume completed after timeout
          hideModal();
        }
        
      } catch (error) {
        console.error('SessionCreationContext: Error polling status:', error);
        attemptsRef.current++;
        
        if (attemptsRef.current >= maxAttempts) {
          stopPolling();
          setStatus('completed');
          hideModal();
        }
      }
    };
    
    // Initial poll
    poll();
    
    // Set up interval
    intervalRef.current = setInterval(poll, 2000);
  };

  // Stop polling
  const stopPolling = () => {
    console.log('SessionCreationContext: Stopping polling');
    setIsPolling(false);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  // Show modal when user navigates during session creation
  const showModalIfCreating = () => {
    console.log('SessionCreationContext: Checking if should show modal, status:', status);
    if (status === 'creating') {
      console.log('SessionCreationContext: Showing modal');
      setShowModal(true);
    }
  };

  // Hide modal
  const hideModal = () => {
    console.log('SessionCreationContext: Hiding modal');
    setShowModal(false);
  };

  // Trigger session creation (called after diagnostic submit)
  const triggerSessionCreation = () => {
    console.log('SessionCreationContext: Triggering session creation');
    setStatus('creating');
    startPolling();
  };

  // Reset everything
  const reset = () => {
    console.log('SessionCreationContext: Resetting');
    stopPolling();
    setStatus('not_started');
    hideModal();
    attemptsRef.current = 0;
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, []);

  const value = {
    status,
    showModal,
    isPolling,
    startPolling,
    stopPolling,
    showModalIfCreating,
    hideModal,
    triggerSessionCreation,
    reset
  };

  return (
    <SessionCreationContext.Provider value={value}>
      {children}
    </SessionCreationContext.Provider>
  );
};